/**
 * Viewport service — single source of truth for responsive layout decisions.
 *
 * Layout is driven by the shorter edge (min of width/height) so a phone held in
 * landscape still gets mobile density, and rotating only re-flows once. Touch
 * capability gates gesture handlers but does NOT change layout — a touch-capable
 * Chromebook in landscape still shows the desktop layout.
 */
import type { Application } from 'pixi.js';

export type LayoutMode = 'desktop' | 'tablet' | 'mobile';
export type Orientation = 'landscape' | 'portrait';

export interface ViewportInfo {
  w: number;
  h: number;
  mode: LayoutMode;
  orientation: Orientation;
  isTouch: boolean;
  /** Safe-area insets in CSS pixels (iOS notch / Android gesture bar). */
  safeTop: number;
  safeRight: number;
  safeBottom: number;
  safeLeft: number;
}

// Breakpoints apply to the SHORTER edge. Phone in landscape (844x390) -> mobile.
const MOBILE_MAX = 699;
const TABLET_MAX = 999;

const IS_TOUCH: boolean =
  typeof window !== 'undefined' &&
  ((window.navigator?.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window);

function classifyMode(w: number, h: number): LayoutMode {
  const short = Math.min(w, h);
  if (short <= MOBILE_MAX) return 'mobile';
  if (short <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

function readSafeAreaInsets(): { top: number; right: number; bottom: number; left: number } {
  if (typeof document === 'undefined') return { top: 0, right: 0, bottom: 0, left: 0 };
  const cs = getComputedStyle(document.documentElement);
  const px = (v: string) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    top:    px(cs.getPropertyValue('--safe-top')),
    right:  px(cs.getPropertyValue('--safe-right')),
    bottom: px(cs.getPropertyValue('--safe-bottom')),
    left:   px(cs.getPropertyValue('--safe-left')),
  };
}

let cachedInsets = readSafeAreaInsets();

export function getViewport(app: Application): ViewportInfo {
  const w = app.screen.width;
  const h = app.screen.height;
  return {
    w,
    h,
    mode: classifyMode(w, h),
    orientation: h > w ? 'portrait' : 'landscape',
    isTouch: IS_TOUCH,
    safeTop: cachedInsets.top,
    safeRight: cachedInsets.right,
    safeBottom: cachedInsets.bottom,
    safeLeft: cachedInsets.left,
  };
}

type Listener = (vp: ViewportInfo) => void;
const listeners = new Set<Listener>();
let boundApp: Application | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastSnapshot: string | null = null;
let listenersInstalled = false;

function snapshotKey(vp: ViewportInfo): string {
  return `${vp.w}x${vp.h}|${vp.mode}|${vp.orientation}|${vp.safeTop},${vp.safeRight},${vp.safeBottom},${vp.safeLeft}`;
}

function fire() {
  if (!boundApp) return;
  cachedInsets = readSafeAreaInsets();
  const vp = getViewport(boundApp);
  const key = snapshotKey(vp);
  if (key === lastSnapshot) return;
  lastSnapshot = key;
  for (const cb of listeners) cb(vp);
}

function scheduleFire() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fire, 100);
}

/**
 * Install window listeners (idempotent). Call once during app init.
 * Pass the PixiJS Application that owns the canvas being observed.
 */
export function initViewport(app: Application): ViewportInfo {
  boundApp = app;
  cachedInsets = readSafeAreaInsets();
  lastSnapshot = snapshotKey(getViewport(app));

  if (typeof window !== 'undefined' && !listenersInstalled) {
    window.addEventListener('resize', scheduleFire);
    window.addEventListener('orientationchange', scheduleFire);
    listenersInstalled = true;
  }
  return getViewport(app);
}

/** Subscribe to viewport changes. Returns an unsubscribe fn. */
export function onViewportChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
